'use client';

import { getFragmentData } from '@packages/gql/generated/shopping';
import { RecoProductCardFragmentDoc } from '@packages/gql/generated/shopping/RecoProductCardFragmentDoc';
import { SearchBasketToProductsDocument } from '@packages/gql/generated/shopping/SearchBasketToProductsDocument';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from 'urql';
import { useRecoElement } from '../../../hooks/useRecoElement';
import type { ProductsRecoSliderProps, RdeType, RequestParams } from '../../../types';
import { RecoCarousel } from '../../RecoCarousel/RecoCarousel.client';
import { RecoProductCard } from '../../RecoProductCard/RecoProductCard.client';
import { useProductsRecoTracking } from '../../RecoProductCard/useProductsRecoTracking';

export type SearchBasketToProductsRecoSliderProps = Pick<RequestParams, 'aklIds'> &
  ProductsRecoSliderProps;

/* GraphQL */ `
  query SearchBasketToProducts($aklIds: [String!]!, $locale: String!) {
    searchBasketToProducts(aklIds: $aklIds, locale: $locale) {
      title
      type
      recommendations {
        ...RecoProductCard
      }
    }
  }
`;

const RDE_TYPE: RdeType = 'searchBasketToProducts';

/**
 * SearchSerpToProductRecoSlider component description displayed in storybook
 * */
export const SearchBasketToProductsRecoSlider = ({
  backgroundColor,
  fetchOnThreshold = false,
  onProductClick,
  onSeen,
  preferTitle = false,
  aklIds,
  title,
}: SearchBasketToProductsRecoSliderProps) => {
  const { locale } = useIntl();
  const { isInThreshold, isInView, recoEl } = useRecoElement();

  const [{ data, error }] = useQuery({
    query: SearchBasketToProductsDocument,
    variables: {
      locale,
      aklIds,
    },
    context: useMemo(
      () => ({ suspense: false, fetchOptions: { headers: { 'x-locale': locale } } }),
      [locale],
    ),
    pause: fetchOnThreshold ? !isInThreshold : false,
    requestPolicy: 'network-only',
  });

  const recommendations = data ? data.searchBasketToProducts.recommendations : undefined;

  /** Filtered out recommendations that don't have a product (product === null) */
  const filteredRecommendations = recommendations
    ? recommendations.filter(
        (recommendation) => getFragmentData(RecoProductCardFragmentDoc, recommendation).product,
      )
    : undefined;

  const rdeTypeAbbr = data?.searchBasketToProducts.type;

  useProductsRecoTracking({
    rdeType: RDE_TYPE,
    rdeTypeAbbr,
    recommendations: filteredRecommendations,
    title: data?.searchBasketToProducts.title || '',
    trackingCondition: isInView && !!filteredRecommendations && filteredRecommendations.length > 0,
    onSeen,
  });

  if (
    (error && !filteredRecommendations) ||
    (filteredRecommendations && filteredRecommendations.length === 0)
  ) {
    return null;
  }

  return (
    <RecoCarousel
      title={preferTitle ? title : data?.searchBasketToProducts.title || title}
      loadingState={!filteredRecommendations}
      backgroundColor={backgroundColor}
      ref={recoEl}
    >
      {filteredRecommendations?.map((recommendation, idx) => (
        <RecoProductCard
          // rule is disabled because order doesn't change between rerenders
          // eslint-disable-next-line react/no-array-index-key
          key={idx}
          data={recommendation}
          onClick={(akl, sku) => {
            onProductClick?.(akl, sku);
          }}
          position={idx + 1}
          rdeTypeAbbr={rdeTypeAbbr}
        />
      ))}
    </RecoCarousel>
  );
};
